import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Alert from '../components/ui/Alert';
import { useDocuments } from '../contexts/DocumentContext';
import './EditorPage.css';
import './ViewDocumentPage.css';

// Set up the worker for PDF.js
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const ViewDocumentPage = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const { documents, loading, error: contextError } = useDocuments();
  const documentContainerRef = useRef(null);
  
  const [document, setDocument] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [fields, setFields] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pages, setPages] = useState([]);

  // Load document data
  useEffect(() => {
    if (documents.length > 0) {
      const doc = documents.find(d => d.id === documentId);
      if (doc) {
        setDocument(doc);
        // Make a deep copy of the fields to avoid reference issues
        setFields(doc.fields ? JSON.parse(JSON.stringify(doc.fields)) : []);
        setIsLoading(false);
      } else {
        setError('Document not found');
        setIsLoading(false);
      }
    }
  }, [documentId, documents]);

  // Handle PDF document loading
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPages(Array.from(new Array(numPages), (_, index) => index + 1));
  };

  // Combined loading and document check to prevent flashing error messages
  if (isLoading || loading || (!document && documents.length > 0)) {
    return (
      <div className="editor-page">
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading document viewer...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || contextError) {
    return (
      <div className="editor-page">
        <div className="container">
          <Alert 
            type="error" 
            title="Error" 
            message={error || contextError} 
          />
          <div className="text-center mt-lg">
            <Button onClick={() => navigate('/dashboard')}>
              Return to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Only show "Document Not Found" after we're sure it's not still loading
  if (!document && !isLoading && !loading && documents.length > 0) {
    return (
      <div className="editor-page">
        <div className="container">
          <Alert 
            type="error" 
            title="Document Not Found" 
            message="The document you're looking for doesn't exist or has been deleted." 
          />
          <div className="text-center mt-lg">
            <Button onClick={() => navigate('/dashboard')}>
              Return to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // If document is not yet loaded, show loading
  if (!document) {
    return (
      <div className="editor-page">
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Preparing document for viewing...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="editor-page">
      <div className="container">
        <div className="editor-header">
          <h1 className="page-title">{document.name}</h1>
          <div className="editor-actions">
            <Button 
              variant="secondary" 
              onClick={() => navigate('/dashboard')}
            >
              Back to Dashboard
            </Button>
          </div>
        </div>
        
        <div className="editor-content">
          <div className="editor-sidebar">
            <Card className="editor-instructions">
              <h3>Document Information</h3>
              <p className="mt-md">
                <strong>Status:</strong> <span className="document-status status-sent">Awaiting Signature</span>
              </p>
              <p className="mt-sm">
                <strong>Recipient:</strong> {document.recipientEmail || document.recipient_email || 'Not specified'}
              </p>
              <p className="mt-sm">
                <strong>Total Pages:</strong> {numPages}
              </p>
              <div className="mt-md">
                <p>This document has been sent for signature and is awaiting completion.</p>
                <p className="mt-sm">You can view the document and signature fields, but cannot edit them at this stage.</p>
              </div>
            </Card>
          </div>
          
          <div className="document-preview" ref={documentContainerRef}>
            <Document
              file={document.file_data && document.file_data.startsWith('STORAGE_URL:') 
                ? document.file_data.substring('STORAGE_URL:'.length) 
                : document.fileData}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={<div className="loading-spinner"></div>}
              error={<Alert type="error" message="Failed to load PDF document" />}
            >
              <div className="document-pages-container">
                {pages.map(pageNumber => (
                  <div key={pageNumber} className="document-page-wrapper">
                    <div className="document-page-overlay">
                      {fields
                        .filter(field => field.pageNumber === pageNumber)
                        .map(field => (
                          <div
                            key={field.id}
                            className={`signature-field signature-field-${field.type} readonly`}
                            style={{
                              left: `${field.position.x}px`,
                              top: `${field.position.y}px`,
                              width: field.type === 'signature' ? '150px' : field.type === 'initial' ? '60px' : '120px',
                              height: field.type === 'signature' ? '60px' : field.type === 'initial' ? '40px' : '40px',
                            }}
                          >
                            <div className="field-label">
                              {field.type === 'signature' ? 'Signature' : 
                               field.type === 'initial' ? 'Initial' : 
                               field.type === 'date' ? 'Date' : 'Field'}
                            </div>
                          </div>
                        ))
                      }
                    </div>
                    <Page 
                      pageNumber={pageNumber} 
                      width={800}
                      className="document-page"
                    />
                  </div>
                ))}
              </div>
            </Document>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewDocumentPage;
